import React from 'react';
import { Search, Filter, MapPin, Check } from 'lucide-react';
import { PaymentMethod } from '../../types/index';
import { traduzirMetodoPagamento } from '../../shared/logica/pagamentos';

function FilterSection({ title, options, selected, onToggle }) {
  return (
    <div className="mb-4">
      <p className="text-[10px] font-black text-slate-400 uppercase mb-2 px-1">{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button 
            key={opt.value || opt} 
            onClick={() => onToggle(opt.value || opt)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all border ${
              selected.includes(opt.value || opt) 
                ? 'bg-green-100 text-green-800 border-green-200' 
                : 'bg-slate-50 text-slate-400 border-slate-100'
            }`}
          >
            {opt.label || opt.replace('_', ' ')}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Componente de pesquisa e filtros para movimentos
 */
export default function FiltrosMovimentos({
  search,
  onSearchChange,
  showFilters,
  onToggleFilters,
  filterTypes,
  filterMethods,
  filterInvoice,
  filterDeliveryPending,
  filterDeliveryDelivered,
  filterUsers,
  onFilterTypesChange,
  onFilterMethodsChange,
  onFilterInvoiceChange,
  onFilterDeliveryPendingChange,
  onFilterDeliveryDeliveredChange,
  onFilterUsersChange,
  onClearFilters,
  users = []
}) {
  const hasActiveFilters = filterTypes.length + filterMethods.length + filterInvoice.length + filterUsers.length > 0 || filterDeliveryPending || filterDeliveryDelivered;

  const userOptions = users.map(u => ({ value: u.id, label: u.name }));

  return (
    <div className="flex gap-4 items-center">
      <div className="relative flex-1 group">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-green-600 transition-colors" size={20} />
        <input 
          type="text" 
          placeholder="Pesquisar por Cliente, Fatura, Produto, Data (ex: 01/01/2024)..."
          className="w-full pl-14 pr-4 py-4 bg-white border-2 border-slate-100 rounded-2xl shadow-sm outline-none focus:border-green-500 font-bold text-lg"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>
      
      <div className="relative">
        <button 
          onClick={onToggleFilters}
          className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-black border-2 transition-all ${
            showFilters || hasActiveFilters 
              ? 'bg-green-800 border-green-800 text-white' 
              : 'bg-white border-slate-100 text-slate-500'
          }`}
        >
          <Filter size={20} /> Filtros {hasActiveFilters && `(${filterTypes.length + filterMethods.length + filterInvoice.length + filterUsers.length + (filterDeliveryPending ? 1 : 0) + (filterDeliveryDelivered ? 1 : 0)})`}
        </button>

        {showFilters && (
          <div className="absolute right-0 mt-3 w-80 bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 z-40">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-black text-slate-900 uppercase text-xs tracking-widest">Filtros Avançados</h3>
              <button onClick={onClearFilters} className="text-[10px] font-black text-green-700 uppercase">
                Limpar
              </button>
            </div>
            
            <FilterSection 
              title="Tipo" 
              options={['VENDA', 'SERVICO', 'GAS']} 
              selected={filterTypes} 
              onToggle={(val) => onFilterTypesChange(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])} 
            />
            <FilterSection 
              title="Método" 
              options={[
                { value: PaymentMethod.CASH, label: traduzirMetodoPagamento(PaymentMethod.CASH) },
                { value: PaymentMethod.CARD, label: traduzirMetodoPagamento(PaymentMethod.CARD) },
                { value: PaymentMethod.TRANSFER, label: traduzirMetodoPagamento(PaymentMethod.TRANSFER) },
                { value: PaymentMethod.CHEQUE, label: traduzirMetodoPagamento(PaymentMethod.CHEQUE) },
                { value: PaymentMethod.PENDING, label: traduzirMetodoPagamento(PaymentMethod.PENDING) },
                { value: PaymentMethod.VOUCHER, label: traduzirMetodoPagamento(PaymentMethod.VOUCHER) },
                { value: PaymentMethod.MBWAY, label: traduzirMetodoPagamento(PaymentMethod.MBWAY) }
              ]} 
              selected={filterMethods} 
              onToggle={(val) => onFilterMethodsChange(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])} 
            />
            <FilterSection 
              title="Faturação" 
              options={['FATURADO', 'NAO_FATURADO', 'PARA_FATURAR']} 
              selected={filterInvoice} 
              onToggle={(val) => onFilterInvoiceChange(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])} 
            />
            
            {userOptions.length > 0 && (
              <FilterSection 
                title="Utilizador" 
                options={userOptions} 
                selected={filterUsers} 
                onToggle={(val) => onFilterUsersChange(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])} 
              />
            )}
            
            <div className="pt-4 border-t border-slate-50 space-y-2">
              <button 
                onClick={() => onFilterDeliveryPendingChange(!filterDeliveryPending)}
                className={`w-full flex items-center justify-between p-3 rounded-xl font-bold text-sm ${
                  filterDeliveryPending ? 'bg-blue-50 text-blue-700' : 'text-slate-400 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <MapPin size={16} /> Para Entregar
                </span>
                {filterDeliveryPending && <Check size={16} />}
              </button>
              <button 
                onClick={() => onFilterDeliveryDeliveredChange(!filterDeliveryDelivered)}
                className={`w-full flex items-center justify-between p-3 rounded-xl font-bold text-sm ${
                  filterDeliveryDelivered ? 'bg-green-50 text-green-700' : 'text-slate-400 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <MapPin size={16} /> Entregue
                </span>
                {filterDeliveryDelivered && <Check size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



